import React, { useState, useRef, useEffect } from 'react'
import { ChevronUp, ChevronLeft, ChevronRight, Info, List, Trash2 } from 'lucide-react'
import { Detection } from '@/types'
import { cn } from '@/utils/cn'
import { formatClassNameForDisplay } from '@/utils/classNameUtils'
import { getClassColor as getCategoryColor, colorWithOpacity } from '@/utils/colorUtils'
import { useCategoryContext } from '@/contexts/CategoryContext'
import ThumbnailPreview from './ThumbnailPreview'
import Tooltip from './Tooltip'


// Function to generate consistent colors for bounding boxes/classes
const getTooltipClassColor = (classIndex: number): string => {
  const colors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
    '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
    '#F8C471', '#82E0AA', '#F1948A', '#85CDFD', '#D7DBDD'
  ]
  return colors[classIndex % colors.length]
}

// Function to get detection classes with counts and colors
const getDetectionClassesSummary = (detections: Detection[]): { class: string, count: number, color: string }[] => {
  const classCounts = new Map<string, number>()
  
  // Count occurrences of each top1 class (or user-corrected class)
  detections.forEach(detection => {
    const topClass = detection.user_corrected_class || 
                    (detection.top_classes && detection.top_classes.length > 0 ? 
                     detection.top_classes[0].label : 'Unknown') || 'Unknown'
    // Use class name directly - no more mapping needed
    classCounts.set(topClass, (classCounts.get(topClass) || 0) + 1)
  })
  
  // Convert to array with colors
  return Array.from(classCounts.entries()).map(([className, count], index) => ({
    class: formatClassNameForDisplay(className),
    count,
    color: getTooltipClassColor(index)
  }))
}

// Function to get confidence color based on percentage
const getConfidenceColor = (confidence: number): string => {
  const percentage = confidence * 100
  if (percentage >= 90) {
    return 'bg-green-600'
  } else if (percentage >= 70) {
    return 'bg-orange-600'
  } else {
    return 'bg-red-600'
  }
}

// Function to get class color (avoiding orange and green which are reserved)
const getClassColor = (classLabel: string | undefined | null): string => {
  const colorMap: Record<string, string> = {
    'red': 'border-red-500 bg-red-500/20 hover:border-red-600 hover:bg-red-500/30',
    'blue': 'border-blue-500 bg-blue-500/20 hover:border-blue-600 hover:bg-blue-500/30',
    'purple': 'border-purple-500 bg-purple-500/20 hover:border-purple-600 hover:bg-purple-500/30',
    'pink': 'border-pink-500 bg-pink-500/20 hover:border-pink-600 hover:bg-pink-500/30',
    'indigo': 'border-indigo-500 bg-indigo-500/20 hover:border-indigo-600 hover:bg-indigo-500/30',
    'cyan': 'border-cyan-500 bg-cyan-500/20 hover:border-cyan-600 hover:bg-cyan-500/30',
    'teal': 'border-teal-500 bg-teal-500/20 hover:border-teal-600 hover:bg-teal-500/30',
    'yellow': 'border-yellow-500 bg-yellow-500/20 hover:border-yellow-600 hover:bg-yellow-500/30'
  }

  const colorNames = Object.keys(colorMap)

  // Handle undefined or null classLabel
  if (!classLabel) {
    return colorMap.blue // Default color for undefined labels
  }

  // Create a simple hash from the class label
  let hash = 0
  for (let i = 0; i < classLabel.length; i++) {
    hash = classLabel.charCodeAt(i) + ((hash << 5) - hash)
  }

  const colorName = colorNames[Math.abs(hash) % colorNames.length]
  return colorMap[colorName]
}

interface ImageViewerProps {
  imageFile?: File
  imageUrl?: string
  detections: Detection[]
  className?: string
  topK?: number
  onClassOverride?: (detectionId: number, newTopClass: string, newScore: number) => void
  isDrawingMode?: boolean
  onUserDrawnDetection?: (
    bbox: [number, number, number, number],
    imageElement: HTMLImageElement
  ) => void
  onDeleteDetection?: (detectionId: number) => void
  autoOpenDetectionId?: number | null // New prop
  onAutoOpenDone?: () => void // New prop
  // Navigation props
  onPrevImage?: () => void
  onNextImage?: () => void
  hasPrevImage?: boolean
  hasNextImage?: boolean
  isAdmin?: boolean // New prop for admin-only features
  imageFileName?: string // New prop for filename display
  classValidationStatus?: Map<string, 'match' | 'mismatch' | 'unexpected'> // New prop for validation
}

interface BoundingBoxProps {
  detection: Detection
  imageWidth: number
  imageHeight: number
  scale: number
  offsetX: number
  offsetY: number
  onToggleDropdown: (id: number) => void
  isDropdownOpen: boolean
  topK?: number
  onClassOverride?: (detectionId: number, newTopClass: string, newScore: number) => void
  onDeleteDetection?: (detectionId: number) => void
  containerDimensions: { width: number; height: number }
  isAdmin?: boolean
  getClassCategory: (className: string) => any
  classValidationStatus?: Map<string, 'match' | 'mismatch' | 'unexpected'>
}

const BoundingBox: React.FC<BoundingBoxProps> = ({
  detection,
  imageWidth,
  imageHeight,
  scale,
  offsetX,
  offsetY,
  onToggleDropdown,
  isDropdownOpen,
  topK = 5,
  onClassOverride,
  onDeleteDetection,
  containerDimensions,
  isAdmin = false,
  getClassCategory,
  classValidationStatus,
}) => {
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null)
  
  // Close context menu on outside click
  useEffect(() => {
    const handleClickOutside = (_event: MouseEvent) => {
      if (contextMenu) {
        setContextMenu(null)
      }
    }

    if (contextMenu) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [contextMenu])

  // Store original top1 class for reference
  const originalTop1Class = (detection as any).original_top1_class || 
    (detection.top_classes && detection.top_classes.length > 0 ? detection.top_classes[0].label : null)
  const [x1, y1, x2, y2] = detection.bbox
  
  // Check if coordinates are normalized (0-1) or pixel coordinates
  const isNormalized = x1 <= 1 && y1 <= 1 && x2 <= 1 && y2 <= 1
  
  let left, top, width, height
  
  if (isNormalized) {
    // Convert from normalized coordinates to pixel coordinates
    left = x1 * imageWidth * scale + offsetX
    top = y1 * imageHeight * scale + offsetY
    width = (x2 - x1) * imageWidth * scale
    height = (y2 - y1) * imageHeight * scale
  } else {
    // Coordinates are already in pixels, just scale them
    left = x1 * scale + offsetX
    top = y1 * scale + offsetY
    width = (x2 - x1) * scale
    height = (y2 - y1) * scale
  }


  // Get top1 class for color assignment
  const top1Class = detection.top_classes && detection.top_classes.length > 0
    ? (detection.top_classes[0].class_name || detection.top_classes[0].label)
    : detection.original_top1_class || 'unknown'

  // Get category for color
  const category = top1Class ? getClassCategory(top1Class) : null
  const classColor = getCategoryColor(top1Class || 'unknown', category)

  const isUserCorrected = detection.is_user_corrected
  const isUserDrawn = detection.user_drawn
  
  // Debug: Log detection state for uncertain detections
  if (detection.quality_score !== undefined && detection.quality_score < 0.01) {
    console.log('🔍 ImageViewer rendering detection:', {
      id: detection.id,
      is_user_corrected: detection.is_user_corrected,
      passes_refiner: detection.passes_refiner,
      quality_score: detection.quality_score,
      isUncertain: 'will calculate below'
    });
  }
  
  // Detection is certain if:
  // - It passes ALL threshold checks (ArcFace cutoff + refiner quality), OR
  // - User has corrected/confirmed it (making it resolved/confident)
  const passesAllThresholds = detection.passes_cutoff !== false && detection.passes_refiner !== false
  const isCertain = passesAllThresholds || detection.is_user_corrected
  
  // Detection is uncertain if it fails thresholds AND is not user-corrected
  // User-drawn detections can still be uncertain if they fail thresholds
  const isUncertain = (!passesAllThresholds) && 
                      !detection.is_user_corrected
  
  // Calculate inline styles for detection box
  let boxStyle: React.CSSProperties = {
    borderColor: classColor,
    backgroundColor: colorWithOpacity(classColor, 0.2),
  }

  // User corrected: double border and shadow
  if (isUserCorrected && !isUserDrawn) {
    boxStyle.borderWidth = '4px'
    boxStyle.borderStyle = 'double'
    boxStyle.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
  }

  // Uncertain: orange dashed border with reduced opacity
  if (isUncertain) {
    boxStyle.borderColor = '#f97316' // orange-500
    boxStyle.backgroundColor = 'rgba(254, 215, 170, 0.5)' // orange-100 with opacity
    boxStyle.borderStyle = 'dashed'
    boxStyle.opacity = detection.confidence && detection.confidence < 0.3 ? 0.3 : 0.5
  }
  
  // Get top1 confidence for display (classification confidence instead of detection confidence)
  const classificationConfidence = detection.top_classes && detection.top_classes.length > 0 
    ? (detection.top_classes[0].score !== undefined ? detection.top_classes[0].score :
       detection.top_classes[0].distance !== undefined ? Math.max(0, 1 - detection.top_classes[0].distance) :
       detection.confidence || 0)
    : (detection.confidence || 0)

  // Calculate container bounds for dropdown positioning
  const containerWidth = containerDimensions.width || 800
  const containerHeight = containerDimensions.height || 600
  const dropdownWidth = 320 // min-w-64 = 256px, max-w-80 = 320px
  const estimatedDropdownHeight = 400 // estimated height for positioning
  
  // Position dropdown to the right of the bounding box, or left if too close to edge
  let dropdownLeft = left + width + 10
  let dropdownTop = top
  
  // Keep dropdown within container horizontal bounds
  if (dropdownLeft + dropdownWidth > containerWidth) {
    dropdownLeft = Math.max(10, left - dropdownWidth - 10) // Position to the left
  }
  
  // Keep dropdown within container vertical bounds
  // Check if dropdown would extend beyond bottom
  if (dropdownTop + estimatedDropdownHeight > containerHeight - 20) {
    // Position above the detection box
    dropdownTop = top - estimatedDropdownHeight - 10
  }
  
  // Ensure dropdown doesn't go above container top
  if (dropdownTop < 10) {
    dropdownTop = 10
  }
  
  // Final check: if it still extends beyond bottom, force it up more
  if (dropdownTop + estimatedDropdownHeight > containerHeight - 20) {
    dropdownTop = containerHeight - estimatedDropdownHeight - 20
  }

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setContextMenu(null) // Close context menu on regular click
    onToggleDropdown(detection.id)
  }

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    // Get mouse position relative to the document and ensure it stays within viewport
    const contextMenuWidth = 160 // Approximate width of context menu
    const contextMenuHeight = 40 // Approximate height of context menu
    
    let x = e.clientX
    let y = e.clientY
    
    // Ensure context menu doesn't go off-screen
    if (x + contextMenuWidth > window.innerWidth) {
      x = window.innerWidth - contextMenuWidth - 10
    }
    if (y + contextMenuHeight > window.innerHeight) {
      y = window.innerHeight - contextMenuHeight - 10
    }
    
    setContextMenu({ x, y })
  }

  const handleDeleteDetection = () => {
    if (onDeleteDetection) {
      onDeleteDetection(detection.id)
    }
    setContextMenu(null)
  }

  const handleClassSelect = (classLabel: string, score: number) => {
    if (onClassOverride) {
      onClassOverride(detection.id, classLabel, score)
    }
  }

  return (
    <>
      {/* Bounding Box */}
      <div
        className="absolute border-4 cursor-pointer transition-all duration-200 z-10 group/box"
        style={{
          left: `${left}px`,
          top: `${top}px`,
          width: `${width}px`,
          height: `${height}px`,
          minWidth: '20px',
          minHeight: '20px',
          ...boxStyle,
        }}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
      >
        {/* Class Name Badge - shown on hover with smaller font */}
        <div className={cn(
          "absolute -top-5 right-0 text-white px-1.5 py-0.5 rounded flex items-center gap-0.5",
          "opacity-0 group-hover/box:opacity-100 transition-opacity duration-200",
          "text-[10px] leading-tight",
          (() => {
            // Priority: user correction > validation > default
            if (isUserCorrected && !isUserDrawn) return "bg-purple-600"

            // Check validation status
            const validationStatus = top1Class ? classValidationStatus?.get(top1Class) : undefined
            if (validationStatus === 'match') return "bg-green-600"
            if (validationStatus === 'mismatch') return "bg-orange-600"
            if (validationStatus === 'unexpected') return "bg-red-600"

            // Default
            return "bg-blue-600"
          })()
        )}>
          {isUserDrawn && <span title="User drawn">✏️</span>}
          {isUserCorrected && !isUserDrawn && <span title="User corrected">✓</span>}
          <ThumbnailPreview className={top1Class || 'Unknown'}>
            {formatClassNameForDisplay(top1Class || 'Unknown')}
          </ThumbnailPreview>
        </div>
      </div>

      {/* Dropdown */}
      {isDropdownOpen && (
        <div
          className="absolute z-50 bg-white border border-gray-200 rounded-lg shadow-lg min-w-64 max-w-80"
          style={{
            left: `${dropdownLeft}px`,
            top: `${dropdownTop}px`,
          }}
          onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
        >
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-gray-100">
            <div>
              <div className="space-y-1">
                {isAdmin ? (
                  <p className="text-sm text-purple-600 font-medium">
                    Confidence: {isNaN(classificationConfidence) ? "0.0" : (classificationConfidence * 100).toFixed(1)}%
                  </p>
                ) : (
                  <p className={cn(
                    "text-sm font-medium",
                    detection.is_confident ? "text-green-700" : "text-orange-700"
                  )}>
                    Status: {detection.is_confident ? "Confident" : "Uncertain"}
                  </p>
                )}
                {/* Admin-only scores in purple */}
                {isAdmin && (
                  <>
                    <p className="text-xs text-purple-600 font-medium">
                      Detection Score: {detection.user_drawn ? "Not available, user drawn" : (isNaN(detection.confidence) ? "0.0" : ((detection.confidence || 0) * 100).toFixed(1) + "%")}
                    </p>
                    {detection.quality_score !== undefined && detection.quality_error === null && (
                      <p className="text-xs text-purple-600 font-medium">
                        Quality Score: {(detection.quality_score * 100).toFixed(1)}%
                      </p>
                    )}
                    {detection.quality_error === "disabled" && (
                      <p className="text-xs text-purple-500">
                        Quality estimation is disabled
                      </p>
                    )}
                    {detection.quality_error && detection.quality_error !== "disabled" && (
                      <p className="text-xs text-purple-500">
                        Quality Score: Error
                      </p>
                    )}
                    <p className="text-xs text-purple-600 font-medium">
                      Bounding Box: [{detection.bbox.map(coord => Math.round(coord)).join(', ')}]
                    </p>
                  </>
                )}
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation()
                onToggleDropdown(detection.id)
              }}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <ChevronUp className="h-4 w-4 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-3">
            {/* Status */}
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-600">Status:</span>
              <div className="flex items-center gap-2">
                {isUserCorrected && (
                  <Tooltip
                    content={
                      detection.original_top1_class && (
                        <div className="space-y-1">
                          <p className="font-medium text-purple-200">Original AI Prediction:</p>
                          <p className="text-purple-100">{formatClassNameForDisplay(detection.original_top1_class || 'Unknown')}</p>
                          <p className="font-medium text-purple-200 mt-2">Your Correction:</p>
                          <p className="text-purple-100">{formatClassNameForDisplay(top1Class || 'Unknown')}</p>
                        </div>
                      )
                    }
                    position="top"
                  >
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 flex items-center gap-1 cursor-help">
                      <span>✓</span> User Corrected
                    </span>
                  </Tooltip>
                )}
                <span className={cn(
                  'px-2 py-1 rounded-full text-xs font-medium',
                  isCertain
                    ? 'bg-green-100 text-green-800'
                    : 'bg-orange-100 text-orange-800'
                )}>
                  {isCertain ? 'Confident' : 'Uncertain'}
                </span>
                {/* Accept button for uncertain detections */}
                {isUncertain && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      if (onClassOverride && detection.top_classes && detection.top_classes.length > 0) {
                        const topClass = detection.top_classes[0]
                        onClassOverride(detection.id, topClass.label || topClass.class_name || 'unknown', topClass.score || 0)
                      }
                    }}
                    className={cn(
                      'px-2 py-1 rounded text-xs font-medium flex items-center gap-1',
                      'bg-green-100 text-green-800 hover:bg-green-200 transition-colors',
                      'border border-green-300 hover:border-green-400'
                    )}
                    title="Accept this detection as correct"
                  >
                    <span className="text-xs">✓</span>
                    Accept
                  </button>
                )}
              </div>
            </div>


            {/* Top Classes */}
            {detection.top_classes && detection.top_classes.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Top Matches:
                  {(!isCertain || detection.user_drawn) && (
                    <span className="text-xs text-orange-600 ml-2">(Click to select)</span>
                  )}
                  {isCertain && !detection.user_drawn && (
                    <span className="text-xs text-green-600 ml-2">(Double-click to override)</span>
                  )}
                </p>
                <div className="space-y-2">
                  {detection.top_classes.slice(0, topK).map((cls, clsIndex) => {
                    // Handle both distance-based and score-based formats
                    const classLabel = cls.class_name || cls.label || 'Unknown'
                    const classScore = cls.score !== undefined ? cls.score : 
                                      cls.distance !== undefined ? Math.max(0, 1 - cls.distance) : 0
                    
                    const isOriginalTop1 = originalTop1Class && classLabel === originalTop1Class
                    const isCurrentTop1 = clsIndex === 0
                    
                    return (
                      <div
                        key={clsIndex}
                        className={cn(
                          "flex items-center justify-between p-2 rounded text-sm cursor-pointer transition-colors relative",
                          isCurrentTop1 
                            ? "bg-blue-100 border border-blue-300" 
                            : "bg-gray-50 hover:bg-gray-100",
                          isOriginalTop1 && !isCurrentTop1 && "border border-gray-300 border-dashed"
                        )}
                        onClick={(e) => {
                          e.stopPropagation()
                          if (!isCertain || detection.user_drawn) {
                            handleClassSelect(classLabel, classScore)
                          }
                        }}
                        onDoubleClick={(e) => {
                          e.stopPropagation()
                          if (isCertain && !detection.user_drawn) {
                            handleClassSelect(classLabel, classScore)
                          }
                        }}
                      >
                        <span className="font-medium text-gray-900 truncate flex-1 mr-2">
                          {isCurrentTop1 && (
                            <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                          )}
                          {isOriginalTop1 && !isCurrentTop1 && (
                            <span className="inline-block w-2 h-2 bg-gray-400 rounded-full mr-2" title="Original AI prediction"></span>
                          )}
                          <ThumbnailPreview className={classLabel}>
                            {formatClassNameForDisplay(classLabel)}
                          </ThumbnailPreview>
                          {isOriginalTop1 && !isCurrentTop1 && (
                            <span className="text-xs text-gray-500 ml-1">(AI)</span>
                          )}
                        </span>
                        {isAdmin && (
                          <span className="text-purple-600 font-mono font-medium">
                            {isNaN(classScore) ? "0.0" : (classScore * 100).toFixed(1)}%
                          </span>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed z-[60] bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-32"
          style={{
            left: `${contextMenu.x}px`,
            top: `${contextMenu.y}px`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
            onClick={handleDeleteDetection}
          >
            <Trash2 className="h-4 w-4" />
            Delete Detection
          </button>
        </div>
      )}
    </>
  )
}

export const ImageViewer: React.FC<ImageViewerProps> = ({
  imageFile,
  imageUrl,
  detections,
  className,
  topK = 5,
  onClassOverride,
  isDrawingMode = false,
  onUserDrawnDetection,
  onDeleteDetection,
  autoOpenDetectionId,
  onAutoOpenDone,
  onPrevImage,
  onNextImage,
  hasPrevImage = false,
  hasNextImage = false,
  isAdmin = false,
  imageFileName,
  classValidationStatus,
}) => {
  const { getClassCategory } = useCategoryContext()
  const [finalImageUrl, setFinalImageUrl] = useState<string>('')
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 })
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 })
  const [openDropdowns, setOpenDropdowns] = useState<Set<number>>(new Set())
  const [imageLoaded, setImageLoaded] = useState(false)
  // Drawing mode state
  const [isDrawing, setIsDrawing] = useState(false)
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null)
  const [currentRect, setCurrentRect] = useState<{ x: number; y: number; width: number; height: number } | null>(null)
  
  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  useEffect(() => {
    if (autoOpenDetectionId && onAutoOpenDone) {
      setOpenDropdowns(new Set([autoOpenDetectionId]))
      onAutoOpenDone() // Reset the prop in the parent
    }
  }, [autoOpenDetectionId, onAutoOpenDone])

  useEffect(() => {
    // Reset image loaded state when URL changes
    setImageLoaded(false)
    setImageDimensions({ width: 0, height: 0 })
    setOpenDropdowns(new Set()) // Close any open dropdowns
    
    if (imageFile) {
      // Create object URL for the uploaded file
      const url = URL.createObjectURL(imageFile)
      setFinalImageUrl(url)

      // Cleanup URL when component unmounts
      return () => URL.revokeObjectURL(url)
    } else if (imageUrl) {
      // Use provided URL directly
      setFinalImageUrl(imageUrl)
    }
  }, [imageFile, imageUrl])

  useEffect(() => {
    // Update container dimensions
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        setContainerDimensions({ width: rect.width, height: rect.height })
      }
    }

    updateDimensions()
    window.addEventListener('resize', updateDimensions)
    return () => window.removeEventListener('resize', updateDimensions)
  }, [])

  // ESC key handler for closing detection details
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && openDropdowns.size > 0) {
        setOpenDropdowns(new Set())
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [openDropdowns])


  const handleImageLoad = () => {
    if (imageRef.current) {
      setImageDimensions({
        width: imageRef.current.naturalWidth,
        height: imageRef.current.naturalHeight,
      })
      setImageLoaded(true)
    }
  }

  const handleToggleDropdown = (id: number) => {
    const newOpenDropdowns = new Set(openDropdowns)
    if (newOpenDropdowns.has(id)) {
      newOpenDropdowns.delete(id)
    } else {
      // Close all other dropdowns and open this one
      newOpenDropdowns.clear()
      newOpenDropdowns.add(id)
    }
    setOpenDropdowns(newOpenDropdowns)
  }

  const handleBackgroundClick = () => {
    // Close all dropdowns and context menus when clicking on background
    if (!isDrawingMode) {
      setOpenDropdowns(new Set())
    }
  }

  // Drawing functions
  const getRelativePosition = (e: React.MouseEvent): { x: number; y: number } => {
    if (!containerRef.current) return { x: 0, y: 0 }
    
    const rect = containerRef.current.getBoundingClientRect()
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    }
  }

  const isPointOnImage = (x: number, y: number): boolean => {
    return x >= offsetX && x <= offsetX + displayWidth && 
           y >= offsetY && y <= offsetY + displayHeight
  }

  const convertToImageCoordinates = (x: number, y: number): { x: number; y: number } => {
    // Convert from container coordinates to image coordinates
    const imageX = (x - offsetX) / scale
    const imageY = (y - offsetY) / scale
    return { x: imageX, y: imageY }
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isDrawingMode || !imageRef.current) return
    
    const pos = getRelativePosition(e)
    
    // Only start drawing if clicking on the image
    if (!isPointOnImage(pos.x, pos.y)) return
    
    e.preventDefault()
    setIsDrawing(true)
    setStartPoint(pos)
    setCurrentRect({ x: pos.x, y: pos.y, width: 0, height: 0 })
    setOpenDropdowns(new Set()) // Close any open dropdowns
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDrawing || !startPoint) return
    
    const pos = getRelativePosition(e)
    
    // Calculate rectangle bounds
    const x = Math.min(startPoint.x, pos.x)
    const y = Math.min(startPoint.y, pos.y)
    const width = Math.abs(pos.x - startPoint.x)
    const height = Math.abs(pos.y - startPoint.y)
    
    setCurrentRect({ x, y, width, height })
  }

  const handleMouseUp = (e: React.MouseEvent) => {
    if (!isDrawing || !startPoint || !currentRect || !imageRef.current || !onUserDrawnDetection) {
      setIsDrawing(false)
      setStartPoint(null)
      setCurrentRect(null)
      return
    }
    
    // Only create detection if rectangle has meaningful size
    if (currentRect.width < 10 || currentRect.height < 10) {
      setIsDrawing(false)
      setStartPoint(null)
      setCurrentRect(null)
      return
    }
    
    // Convert container coordinates to image coordinates
    const startImg = convertToImageCoordinates(currentRect.x, currentRect.y)
    const endImg = convertToImageCoordinates(
      currentRect.x + currentRect.width, 
      currentRect.y + currentRect.height
    )
    
    // Ensure coordinates are within image bounds
    const x1 = Math.max(0, Math.min(startImg.x, imageDimensions.width))
    const y1 = Math.max(0, Math.min(startImg.y, imageDimensions.height))
    const x2 = Math.max(0, Math.min(endImg.x, imageDimensions.width))
    const y2 = Math.max(0, Math.min(endImg.y, imageDimensions.height))
    
    // Create bounding box in the format expected by the handler
    const bbox: [number, number, number, number] = [x1, y1, x2, y2]
    
    // Call the handler
    onUserDrawnDetection(bbox, imageRef.current)
    
    // Reset drawing state
    setIsDrawing(false)
    setStartPoint(null)
    setCurrentRect(null)
  }

  // Calculate scaling and positioning for the image
  const scale = Math.min(
    containerDimensions.width / imageDimensions.width,
    containerDimensions.height / imageDimensions.height
  ) || 1

  const displayWidth = imageDimensions.width * scale
  const displayHeight = imageDimensions.height * scale
  const offsetX = (containerDimensions.width - displayWidth) / 2
  const offsetY = (containerDimensions.height - displayHeight) / 2

  return (
    <div className={cn('relative bg-gray-100 rounded-lg group', className)}>
      {/* Drawing mode indicator */}
      {isDrawingMode && (
        <div className="absolute top-2 left-2 z-50 bg-blue-600 text-white px-2 py-1 rounded text-xs font-medium">
          Drawing Mode - Click and drag to create detection
        </div>
      )}

      {/* Navigation Arrows */}
      {(onPrevImage || onNextImage) && (
        <>
          {/* Left Arrow */}
          <button
            onClick={onPrevImage}
            disabled={!hasPrevImage}
            className={cn(
              "absolute left-4 top-1/2 transform -translate-y-1/2 z-40",
              "w-10 h-10 rounded-full flex items-center justify-center",
              "transition-all duration-200",
              hasPrevImage
                ? "bg-black/30 hover:bg-black/60 text-white cursor-pointer"
                : "bg-black/10 text-gray-400 cursor-not-allowed"
            )}
            style={{ top: `${offsetY + displayHeight / 2}px` }}
          >
            <ChevronLeft className="h-6 w-6" />
          </button>

          {/* Right Arrow */}
          <button
            onClick={onNextImage}
            disabled={!hasNextImage}
            className={cn(
              "absolute right-4 top-1/2 transform -translate-y-1/2 z-40",
              "w-10 h-10 rounded-full flex items-center justify-center",
              "transition-all duration-200",
              hasNextImage
                ? "bg-black/30 hover:bg-black/60 text-white cursor-pointer"
                : "bg-black/10 text-gray-400 cursor-not-allowed"
            )}
            style={{ top: `${offsetY + displayHeight / 2}px` }}
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}

      {/* Control buttons */}
      <div
        className="absolute top-4 flex gap-2 z-50"
        style={{ left: `${offsetX + (displayWidth / 2) - 36}px` }}
      >
        {/* Detection classes tooltip */}
        {(() => {
          const classesSummary = getDetectionClassesSummary(detections)
          
          if (classesSummary.length === 0) return null
          
          return (
            <div className="group/button relative">
              <Tooltip 
                content={
                  <div className="space-y-1 text-lg">
                    {classesSummary.map(({ class: className, count, color }, index) => (
                      <div key={index} style={{ color }} className="font-medium">
                        {count}x {className}
                      </div>
                    ))}
                  </div>
                }
                position="bottom"
              >
                <button className="h-8 w-8 text-white bg-black bg-opacity-60 rounded-full p-1 hover:bg-opacity-80 cursor-pointer transition-all flex items-center justify-center">
                  <List className="h-4 w-4" />
                </button>
              </Tooltip>
            </div>
          )
        })()}
        
        {/* Image info tooltip */}
        <div className="group/button relative">
          <Tooltip 
            content={
              <div className="space-y-1 text-lg">
                {imageFileName && <div>{imageFileName}</div>}
                <div>Image: {imageDimensions.width} × {imageDimensions.height}</div>
                <div>Display: {Math.round(displayWidth)} × {Math.round(displayHeight)}</div>
                <div>Scale: {scale.toFixed(3)}</div>
                <div>Offset: {Math.round(offsetX)}, {Math.round(offsetY)}</div>
                <div>Detections: {detections.length}</div>
              </div>
            }
            position="bottom"
          >
            <button className="h-8 w-8 text-white bg-black bg-opacity-60 rounded-full p-1 hover:bg-opacity-80 cursor-pointer transition-all flex items-center justify-center">
              <Info className="h-4 w-4" />
            </button>
          </Tooltip>
        </div>
      </div>
      
      <div
        ref={containerRef}
        className={cn(
          "relative w-full h-full",
          isDrawingMode ? "cursor-crosshair" : ""
        )}
        onClick={handleBackgroundClick} // Close all dropdowns when clicking background
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        style={{ position: 'relative', minHeight: 'inherit' }}
      >
        {finalImageUrl && (
          <>
            {/* Image */}
            <img
              ref={imageRef}
              src={finalImageUrl}
              alt="Uploaded image"
              className="absolute"
              style={{
                left: `${offsetX}px`,
                top: `${offsetY}px`,
                width: `${displayWidth}px`,
                height: `${displayHeight}px`,
                objectFit: 'contain',
              }}
              onLoad={handleImageLoad}
            />


            {/* Bounding Boxes - Only render after image is fully loaded */}
            {imageLoaded && imageDimensions.width > 0 && imageDimensions.height > 0 && detections.map((detection) => (
              <BoundingBox
                key={detection.id}
                detection={detection}
                imageWidth={imageDimensions.width}
                imageHeight={imageDimensions.height}
                scale={scale}
                offsetX={offsetX}
                offsetY={offsetY}
                onToggleDropdown={handleToggleDropdown}
                isDropdownOpen={openDropdowns.has(detection.id)}
                topK={topK}
                onClassOverride={onClassOverride}
                onDeleteDetection={onDeleteDetection}
                containerDimensions={containerDimensions}
                isAdmin={isAdmin}
                getClassCategory={getClassCategory}
                classValidationStatus={classValidationStatus}
              />
            ))}

            {/* Drawing Rectangle */}
            {isDrawing && currentRect && (
              <div
                className="absolute border-2 border-dashed border-blue-500 bg-blue-500/10 z-20 pointer-events-none"
                style={{
                  left: `${currentRect.x}px`,
                  top: `${currentRect.y}px`,
                  width: `${currentRect.width}px`,
                  height: `${currentRect.height}px`,
                }}
              />
            )}
          </>
        )}

        {/* Loading state */}
        {!finalImageUrl && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-gray-500">Loading image...</div>
          </div>
        )}
      </div>

    </div>
  )
}

export default ImageViewer